import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  Ban,
  BedDouble,
  ChevronRight,
  Clock,
  Dumbbell,
  GraduationCap,
  Layers,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  Sparkles,
  UserRound,
  Wind,
  CircleDot,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/thread-lift-mumbai";

const THREAD_LIFT_FAQS = [
  {
    question: "How long do thread lift results last?",
    answer:
      "Results typically last 12 to 18 months depending on the thread type used, the degree of laxity treated, and individual collagen response. PLLA and PCL threads produce longer-lasting results than standard PDO. Most patients choose to repeat treatment annually to maintain the result.",
  },
  {
    question: "Is thread lift painful?",
    answer:
      "Local anesthetic is administered before thread insertion, so the procedure itself is not painful. Patients feel mild pressure or tension during insertion. Post-procedure tenderness at the insertion points lasts 2 to 3 days and is managed with standard pain relief.",
  },
  {
    question: "Can thread lift replace a facelift?",
    answer:
      "No. Thread lift addresses mild to moderate sagging in patients whose skin laxity has not progressed to the degree that requires surgical tissue repositioning and skin removal. For patients with significant jowling, deep tissue descent, or excess skin, a facelift is the appropriate treatment. Dr. Jain will tell you clearly which category you are in during consultation.",
  },
  {
    question: "When will I see results after a thread lift?",
    answer:
      "Lifting is visible immediately after treatment from the mechanical effect of the cog threads. This initial result settles over the first 1 to 2 weeks as swelling resolves. The secondary collagen-building improvement develops over 1 to 3 months.",
  },
  {
    question: "Are there visible marks or scars after thread lift?",
    answer:
      "No. Thread insertion points are 1 to 2 millimeters and require no stitches. They are not visible once initial redness resolves within 24 to 48 hours.",
  },
  {
    question: "Can thread lift be combined with fillers or botulinum toxin?",
    answer:
      "Yes, and this is a common combination. Botulinum toxin relaxes the muscles pulling tissue downward, which works alongside the upward mechanical lift from threads. Fillers address volume loss in areas where descent is accompanied by fat atrophy. The three treatments together address laxity, muscle activity, and volume as a complete non-surgical approach.",
  },
  {
    question: "What are the risks of thread lift?",
    answer:
      "Swelling, bruising, and tenderness at insertion points are expected and temporary. Asymmetry, thread visibility under the skin, and migration are rare and depend on technique. Choosing an experienced practitioner who selects the correct thread type for each anatomical location significantly reduces these risks.",
  },
  {
    question: "How many threads are needed?",
    answer:
      "This depends on the area being treated, the degree of laxity, and the thread type. A single-area treatment such as the jawline alone requires fewer threads than a full-face lift. Dr. Jain determines the number and placement during consultation based on your specific anatomy.",
  },
];

const threadLiftStructuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalProcedure",
      name: "PDO thread lift (non-surgical face lifting)",
      alternateName: ["Thread lift", "Non-surgical facelift", "PDO threads"],
      description:
        "Minimally invasive, non-surgical facial lifting using dissolvable PDO, PLLA, or PCL threads inserted beneath the skin to lift sagging tissue and stimulate collagen. Suitable for early to moderate facial aging as an alternative to facelift when laxity does not require surgery. Not equivalent to surgical facelift for moderate to severe laxity.",
      procedureType: "PercutaneousProcedure",
      bodyLocation: {
        "@type": "AnatomicalStructure",
        name: "Face",
      },
      followup:
        "Days 1–2: mild swelling and tenderness at insertion points. Days 3–5: resume most daily activity; avoid facial massage and strenuous exercise. Weeks 1–2: swelling resolves; lift settles. Months 1–3: collagen stimulation peaks; full result by 3 months.",
      howPerformed:
        "Face marked upright; local anesthetic at entry points; threads inserted via fine cannula (1–2 mm entry points, no sutures); 30–60 minutes; immediate mechanical lift with collagen improvement over 1–3 months.",
      url: PAGE_URL,
      additionalProperty: [
        { "@type": "PropertyValue", name: "Invasiveness", value: "Minimally invasive" },
        { "@type": "PropertyValue", name: "Treatment category", value: "Non-surgical facial rejuvenation" },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: THREAD_LIFT_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    },
  ],
};

function ThreadLiftJsonLd() {
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(threadLiftStructuredData) }} />
  );
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Non-Surgical Treatments", href: "/non-surgical-treatments-in-mumbai" },
    { name: "Thread Lift", href: "/thread-lift-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Non-Surgical · Face</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">
              Thread Lift
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Non-Surgical Face Lifting with PDO Threads by Dr. Vicky Jain
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Thread Lift" icon="⌇" size="hero"  imageSrc={HERO.threadLift} imageAlt="Thread lift"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">What is a Thread Lift?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              A thread lift is a minimally invasive procedure that uses dissolvable threads inserted beneath the skin to
              lift sagging tissue and stimulate collagen production. The threads are placed through small entry points
              using a fine cannula. No incisions, no stitches, no general anesthesia. The procedure takes 30 to 60 minutes
              and most patients return to normal activity within 2 to 5 days.
            </p>
            <p>
              Thread lift works best for patients in the early to moderate stages of facial aging, where tissue has begun
              to descend but has not reached the degree of laxity that requires surgical correction. It produces a visible
              lift immediately and continues to improve skin firmness over 3 to 6 months as the threads stimulate new
              collagen. The threads dissolve over 6 to 12 months, but the collagen response they trigger persists.
            </p>
            <p>
              Dr. Vicky Jain performs thread lift as a standalone treatment and in combination with fillers or
              botulinum toxin for patients who want a more complete non-surgical result. For patients who are not yet ready
              for a facelift, or who want to extend the time before surgery is necessary, thread lift is a clinically
              appropriate option.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">
              Dr. Jain&apos;s Approach to Thread Lift
            </h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                Thread lift produces the best results when the correct thread type is matched to the correct anatomical
                location. Dr. Jain uses cog threads, which have small barbs that anchor into tissue and create mechanical
                lifting, for the cheeks and jawline where structural lift is the primary goal. Smooth threads, which do not
                lift but stimulate collagen and improve skin texture, are used in areas where firmness rather than
                repositioning is needed.
              </p>
              <p>
                The number of threads, their placement angle, and the direction of lift are planned individually for each
                patient. Asymmetric placement is the most common cause of poor thread lift results. Dr. Jain marks the face
                before treatment and adjusts positioning based on how the tissue responds during the procedure. Managing
                expectations is part of every consultation. Thread lift improves early aging. It does not replicate what a
                facelift achieves in moderate to severe cases.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ThreadLiftProblemsSection() {
  const problems = [
    {
      title: "Mild to Moderate Facial Sagging",
      icon: Layers,
      copy: "Thread lift repositions descended tissue in the cheeks and mid-face without surgery. It is most effective when sagging is present but has not progressed to the degree that requires surgical tissue elevation.",
    },
    {
      title: "Early Jowls",
      icon: UserRound,
      copy: "Jowls form as cheek tissue descends below the jawline. Threads placed along the jawline and lower cheek lift this tissue back into position and restore a cleaner jaw contour.",
    },
    {
      title: "Drooping Cheeks",
      icon: Sparkles,
      copy: "Mid-face descent flattens the cheek and creates the appearance of volume loss. Thread lifting repositions the descended cheek pad and restores a lifted mid-face contour without implants or fillers.",
    },
    {
      title: "Loss of Jawline Definition",
      icon: Wind,
      copy: "A soft or undefined jawline is one of the first visible signs of facial aging. Threads along the jaw create mechanical lift that sharpens the jaw contour and improves the lower face profile.",
    },
    {
      title: "Skin Laxity Without Volume Loss",
      icon: CircleDot,
      copy: "Patients who have laxity but adequate facial volume do not need fillers. Thread lift addresses the structural descent without adding unnecessary volume that can create an overfilled appearance.",
    },
    {
      title: "Mild Neck Laxity",
      icon: Ban,
      copy: "Early neck laxity without significant excess skin responds to thread treatment along the neck and jawline. For patients with significant neck sagging, a surgical neck lift is the appropriate correction.",
    },
  ];

  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="thread-lift-problems-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">What Thread Lift Addresses</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {problems.map((card, index) => (
            <div
              key={card.title}
              className="bg-white p-8 border border-[#E8E2D9]"
              data-testid={`thread-lift-problem-card-${index}`}
            >
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ThreadTypesSection() {
  const types = [
    {
      title: "PDO Threads (Polydioxanone)",
      copy: "The most widely used thread material in aesthetic medicine. PDO threads are fully absorbable, have a strong safety record, and dissolve within 6 to 9 months. They stimulate collagen production that continues after the thread has dissolved. Available in smooth, barbed, and cog configurations depending on the treatment goal.",
    },
    {
      title: "Cog Threads",
      copy: "PDO or PLLA threads with small bidirectional barbs that anchor into tissue and create mechanical lifting force. Used in areas where structural repositioning of descended tissue is the goal, primarily the cheeks, jowls, and jawline. These produce the visible lifting component of thread lift treatment.",
    },
    {
      title: "Smooth Threads",
      copy: "Fine threads without barbs that do not lift tissue but stimulate collagen production and improve skin texture and firmness. Used in areas where skin quality improvement is the goal rather than repositioning. Frequently placed in the under-eye area, neck, and across the cheeks as a collagen-boosting treatment.",
    },
    {
      title: "PLLA and PCL Threads",
      copy: "Longer-lasting alternatives to PDO. PLLA threads dissolve over 12 to 18 months, PCL threads over 18 to 24 months. Both continue to stimulate collagen during their longer absorption period. Used for patients who want extended results from a single treatment.",
    },
    {
      title: "Combination with Other Treatments",
      copy: "Thread lift is frequently combined with botulinum toxin to relax muscles that pull facial tissue downward, and with hyaluronic acid fillers to restore volume in areas where descent is accompanied by fat loss. These combinations address both structural laxity and volume simultaneously.",
    },
  ];

  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="thread-types-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-10 text-center">Types of Threads Used</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {types.map((item, index) => (
            <div
              key={item.title}
              className="bg-white p-8 border border-[#E8E2D9]"
              data-testid={`thread-type-card-${index}`}
            >
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowProcedurePerformedSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-procedure-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">How Thread Lift is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Dr. Jain marks the face with the patient sitting upright, assessing where tissue has descended and the optimal
              direction of lift for each area. Local anesthetic is administered at the entry points before any thread
              insertion.
            </p>
            <p>
              Threads are inserted using a fine cannula through small entry points, typically 1 to 2 millimeters in size,
              that require no stitches. The cannula is guided beneath the skin to the planned position, the thread is
              deployed, and the cannula is withdrawn, leaving the thread anchored in the tissue. The entry point is closed
              with gentle pressure. No sutures required.
            </p>
            <p>
              The total procedure takes 30 to 60 minutes depending on the number of threads and areas treated. Patients see
              immediate lifting from the mechanical effect of the cog threads. Over the following 4 to 8 weeks, collagen
              stimulation adds a secondary improvement in skin firmness that builds on the initial result.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Days 1 to 2",
      description: "Mild swelling and tenderness at insertion points. Normal and expected. Cold compresses reduce swelling.",
      icon: BedDouble,
    },
    {
      period: "Days 3 to 5",
      description: "Most patients resume normal daily activity. Avoid facial massage, strenuous exercise, and sleeping on the face.",
      icon: Monitor,
    },
    {
      period: "Weeks 1 to 2",
      description: "Swelling fully resolves. The lifted position settles and results look natural rather than pulled.",
      icon: Dumbbell,
    },
    {
      period: "Months 1 to 3",
      description: "Collagen stimulation builds. Skin firmness improves progressively. Full result is visible at 3 months.",
      icon: Sparkles,
    },
  ];

  return (
    <section className="bg-premium-purple py-14 md:py-20" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">What to Expect</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function WhoIsGoodCandidateSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-is-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Am I a Candidate?</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">
              Who Is a Good Candidate for Thread Lift?
            </h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Thread lift suits patients between 30 and 55 with mild to moderate facial sagging who want a non-surgical
              correction with minimal downtime. Patients who have noticed early jowling, a softer jawline, or slight cheek
              descent but are not ready for or do not need surgical intervention are the primary candidates. Patients who
              want to maintain their result between facelift procedures also benefit from thread treatment.
            </p>
            <p>
              Thread lift is not the right treatment for patients with significant skin laxity, deep tissue descent, or
              excess skin that needs to be removed. In those cases, a facelift produces results that thread lift cannot
              replicate. Dr. Jain is direct about this distinction during consultation. A thread lift that is oversold as a
              facelift alternative for the wrong patient produces disappointment. The procedure works well within its
              appropriate range. Outside that range, surgery is the correct recommendation.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Thread Lift" />;
}

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {THREAD_LIFT_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`item-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">
          Book Your Thread Lift Consultation
        </h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses your skin laxity, tissue descent, and facial volume to determine
          whether thread lift is appropriate for your degree of aging and which thread type and placement strategy produces
          the best result. If surgery is a better fit, he will tell you that directly. You leave with an honest
          recommendation and a clear plan.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Facelift",
      description: "Surgical lifting for patients with moderate to significant facial laxity beyond what threads can address.",
      href: "/facelift-surgery-mumbai",
    },
    {
      name: "Cheek Augmentation",
      description: "Restore mid-face volume alongside thread lifting for a complete non-surgical result.",
      href: "/cheek-augmentation-in-mumbai",
    },
    {
      name: "Blepharoplasty",
      description: "Address eyelid aging alongside lower face lifting for complete facial rejuvenation.",
      href: "/blepharoplasty-eyelid-surgery-mumbai",
    },
  ];

  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function ThreadLiftPage() {
  return (
    <>
      <SEO
        title="Thread Lift | Non-Surgical Facelift by Dr. Vicky Jain"
        description="Early sagging, loose jawline, drooping cheeks. Dr. Vicky Jain performs PDO thread lift with no surgery and minimal downtime. Book your consultation."
        canonical="/thread-lift-mumbai"
      />
      <ThreadLiftJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <ThreadLiftProblemsSection />
      <ThreadTypesSection />
      <HowProcedurePerformedSection />
      <RecoveryTimelineSection />
      <WhoIsGoodCandidateSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
